import React, { useState, useEffect } from 'react';
import { callAI } from '../services/aiService';
import { storageService } from '../services/storageService';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';
import { useToast } from './Toast';

interface CoachingResult {
  overallScore: number;
  strengths: string[];
  improvements: string[];
  rewrittenVersion: string;
}

const STORAGE_KEY_CONTENT = 'coach_original_content';
const STORAGE_KEY_RESULT = 'coach_result';

export const ContentCoach: React.FC = () => {
  const { t } = useT();
  const { toast } = useToast();
  const [originalContent, setOriginalContent] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY_CONTENT) || '';
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<CoachingResult | null>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_RESULT);
    return saved ? JSON.parse(saved) : null;
  });
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_CONTENT, originalContent);
  }, [originalContent]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_RESULT, JSON.stringify(result));
  }, [result]);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const handleAnalyze = async () => {
    if (!originalContent.trim()) return;

    setIsAnalyzing(true);

    try {
      const prompt = `Analyze the following tweet content and provide improvement advice.

【Content to analyze】
${originalContent}

【Output format (JSON)】
{
  "overallScore": score (1-10),
  "strengths": ["strength 1", "strength 2", "strength 3"],
  "improvements": ["improvement 1", "improvement 2", "improvement 3"],
  "rewrittenVersion": "improved version of the tweet"
}`;

      const response = await callAI(
        [
          { role: 'system', content: 'You are an expert at improving social media posts. Provide actionable coaching.' },
          { role: 'user', content: prompt }
        ],
        { temperature: 0.7, maxTokens: 1000, jsonMode: true }
      );

      const parsedResult = JSON.parse(response);
      setResult(parsedResult);

      storageService.addHistoryItem({
        type: 'coaching',
        content: parsedResult.rewrittenVersion.slice(0, 800),
        score: parsedResult.overallScore,
        metadata: {
          strengths: parsedResult.strengths.length,
          improvements: parsedResult.improvements.length,
        },
      });
    } catch (error) {
      console.error('Analysis failed:', error);
      toast(t('common.error'), 'error');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px] mx-auto">
      {/* Header */}
      <div className="mb-6 lg:mb-[30px]">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1.5 tracking-tight text-text-primary">
          {t('contentCoach.title')}
        </h1>
        <p className="text-text-muted text-sm sm:text-[15px] leading-relaxed">
          {t('contentCoach.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Left: Original Content */}
        <div>
          <h2 className="text-lg lg:text-xl mb-3 lg:mb-[15px] text-text-primary">
            {t('contentCoach.inputLabel')}
          </h2>
          <textarea
            value={originalContent}
            onChange={(e) => setOriginalContent(e.target.value)}
            placeholder={t('contentCoach.placeholder')}
            className="w-full min-h-[300px] p-4 lg:p-5 bg-surface-2 border border-border rounded-xl text-text-primary text-base resize-y font-[inherit] leading-[1.6] placeholder:text-text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          />

          <button
            onClick={handleAnalyze}
            disabled={!originalContent.trim() || isAnalyzing}
            className={cn(
              'w-full mt-3 lg:mt-[15px] py-4 border-none rounded-3xl text-base font-bold transition-all',
              !originalContent.trim() || isAnalyzing
                ? 'bg-surface-3 text-text-faint cursor-not-allowed'
                : 'bg-gradient-to-r from-warning to-orange-500 text-white cursor-pointer hover:opacity-90'
            )}
          >
            {isAnalyzing ? t('common.analyzing') : t('contentCoach.getCoaching')}
          </button>
        </div>

        {/* Right: Coaching Results */}
        <div>
          {result ? (
            <div className="space-y-4 lg:space-y-5">
              {/* Overall Score */}
              <div className="p-6 bg-gradient-to-br from-warning to-orange-500 rounded-2xl text-center">
                <div className="text-sm text-white/80 mb-2">{t('contentCoach.overall')}</div>
                <div className="text-5xl font-bold text-white">{result.overallScore}/10</div>
              </div>

              {/* Strengths */}
              <div className="p-5 bg-surface-2 border border-success rounded-xl">
                <h3 className="text-base mb-3 text-success font-bold">
                  {t('contentCoach.coachingAreas')}
                </h3>
                <ul className="m-0 pl-5 text-text-primary space-y-2">
                  {result.strengths.map((strength, index) => (
                    <li key={index} className="leading-[1.5]">{strength}</li>
                  ))}
                </ul>
              </div>

              {/* Improvements */}
              <div className="p-5 bg-surface-2 border border-warning rounded-xl">
                <h3 className="text-base mb-3 text-warning font-bold">
                  {t('contentCoach.suggestion')}
                </h3>
                <ul className="m-0 pl-5 text-text-primary space-y-2">
                  {result.improvements.map((improvement, index) => (
                    <li key={index} className="leading-[1.5]">{improvement}</li>
                  ))}
                </ul>
              </div>

              {/* Rewritten Version */}
              <div className="p-5 bg-surface-2 border-2 border-accent rounded-xl shadow-glow-accent">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-base m-0 text-accent font-bold">
                    {t('contentCoach.improved')}
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => storageService.addSavedIdea(result.rewrittenVersion)}
                      className="px-3 py-1.5 bg-transparent border border-accent rounded-xl text-accent cursor-pointer text-xs font-bold hover:bg-accent/10 transition-colors"
                    >
                      {t('common.save')}
                    </button>
                    <button
                      onClick={() => handleCopy(result.rewrittenVersion, 'rewritten')}
                      className={cn(
                        'px-3 py-1.5 rounded-xl text-xs font-bold transition-all',
                        copiedKey === 'rewritten'
                          ? 'bg-success/20 text-success border border-success'
                          : 'bg-accent text-white border-none cursor-pointer hover:bg-accent/90'
                      )}
                    >
                      {copiedKey === 'rewritten' ? t('common.copied') : t('common.copy')}
                    </button>
                  </div>
                </div>
                <p className="text-[15px] leading-[1.6] m-0 text-text-primary">
                  {result.rewrittenVersion}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[300px] bg-surface-2 border-2 border-dashed border-border rounded-xl text-text-muted text-base text-center">
              {t('contentCoach.emptyState')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
